import React from 'react';

import { studioTheme, readableInk, hexToRgba } from './studioTheme';
import {
  StudioWrap,
  Avatar,
  secondaryLine,
  SourceLogo,
  Stars,
  DateText,
  Engagement,
} from './StudioCardKit';
import { socialCardKeys } from '@/src/constants/socialcard.constant';
import { StudioBody, isMediaTestimonial, StudioMediaCard, hasPicture } from './StudioBody';

/**
 * Colorband (widget type 12)
 *
 * A brand-coloured header band carrying the name and handle, with the avatar
 * overlapping its lower edge. The band colour follows the wall's quotation
 * colour, so the widget picks up the customer's brand without any new field.
 */
const ColorbandCard = (props) => {
  const { cardItem, socialCardType } = props;
  const t = studioTheme(props);

  /* Its own field: repainting the band must not touch any other type. */
  const band = t.bandColor || t.brand;
  const bandInk = readableInk(band);
  const bandMuted = hexToRgba(bandInk === '#ffffff' ? '#ffffff' : '#15171c', 0.75);
  const cardSurface = t.dark ? '#151821' : '#ffffff';

  const bandRole = secondaryLine(cardItem);
  const bandLink = cardItem && cardItem[socialCardKeys.messageLink];

  /* One body type scale per card: the text testimonial and the video/audio
     summary must read as the same widget. */
  const bodyTextStyle = { fontSize: t.smallSize ? 14 : 14.5, lineHeight: 1.55 };

  /* A video or audio testimonial IS the card - the player already carries
     the name and work title, so none of the chrome below is drawn. Only the
     summary keeps this widget's look, as the panel it extends into. */
  if (isMediaTestimonial(cardItem, socialCardType)) {
    return (
      <StudioMediaCard
        cardItem={cardItem}
        socialCardType={socialCardType}
        t={t}
        enclosure={{
          border: `1px solid ${t.line}`,
          borderRadius: 16,
          background: cardSurface,
          /* the wall's custom card colour wins, as it does on the text card */
          ...(t.cardBackgroundObject || {}),
          boxShadow: t.shadow,
        }}
        summaryStyle={bodyTextStyle}
      />
    );
  }

  return (
    <StudioWrap>
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 16,
          border: `1px solid ${t.line}`,
          background: cardSurface,
          ...(t.cardBackgroundObject || {}),
          boxShadow: t.shadow,
          display: 'flex',
          flexDirection: 'column',
        }}
      >

        {/* brand band - carries only the source logo now */}
        <div
          style={{
            background: band,
            color: bandInk,
            padding: '16px 18px 30px',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'flex-start',
            minHeight: 26,
          }}
        >
          <span style={{ color: bandInk, flex: 'none' }}>
            <SourceLogo cardItem={cardItem} />
          </span>
        </div>

        {/* The person straddles the band's bottom edge: the avatar is centred on
            the line, the author name sits in the band above it and the work
            title in the card below it. Each text line is exactly half the
            avatar's height, so the two land on opposite sides of the edge.
            Spelled out instead of reusing the shared identity block because the
            two lines need different colours - one reads on the band, one on the
            card. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px', marginTop: -26 }}>
          <Avatar
            cardItem={cardItem}
            size={52}
            ring={cardSurface}
            hidePlaceholder={hasPicture(cardItem, socialCardType)}
          />
          <div className={t.fontClass} style={{ flex: 1, minWidth: 0 }}>
            <a
              href={bandLink || undefined}
              target={bandLink ? '_blank' : '_self'}
              rel="noreferrer noopener"
              style={{
                display: 'block',
                height: 26,
                lineHeight: '26px',
                color: bandInk,
                fontWeight: 700,
                fontSize: 15,
                textDecoration: 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {cardItem && cardItem[socialCardKeys.authorName]}
            </a>
            {bandRole ? (
              <span
                style={{
                  display: 'block',
                  height: 26,
                  lineHeight: '26px',
                  color: t.muted,
                  fontSize: 12.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {bandRole}
              </span>
            ) : null}
          </div>
        </div>

        <div style={{ padding: '10px 18px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Stars t={t} cardItem={cardItem} size={13} />
          <StudioBody
            t={t}
            cardItem={cardItem}
            socialCardType={socialCardType}
            textStyle={bodyTextStyle}
          />
        </div>

        <div
          style={{
            padding: '8px 18px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <Engagement t={t} cardItem={cardItem} />
          <DateText t={t} cardItem={cardItem} />
        </div>
      </div>
    </StudioWrap>
  );
};

export default ColorbandCard;
